import { IDocument, IPage as IPageUTM, ITranslation, TDomain, TMaintenence, TSettingsAppCookie } from '@naviair-utm/node-shared-interfaces';
import mapboxgl from 'mapbox-gl';

/**
 * Applicable filters for the search
 */
export enum TSettingsFilters {
	SIGMET = 'SIGMET',
	NOTAM_SNOWTAM = 'NOTAM_SNOWTAM',
	AIREP = 'AIREP',
	METAR_TAF = 'METAR_TAF',
}

/**
 * The possible AFTN content types. Used in restricting types of available aftn types in briefing applications
 */
export enum EAftnTypes {
	AIREP = 'airep',
	SIGMET = 'sigmet',
	METAR_TAF = 'metar_taf',
	NOTAM = 'notam',
	SNOWTAM = 'snowtam',
}

/**
 * The properties of a given MapZone
 */
export type TSettingsMapZoneProperties = {
	/**MapZone name */
	name: string;
	/** MapZone link to data regarding the zone */
	link: string;
	/** Type of zone */
	type: 'fir' | 'point' | 'area';
	/** Make the zone visible from a Unix timestamp and until a given Unix timestamp */
	visibleFrom?: number;
	visibleUntil?: number;
	/** Can be used to change the color of the dot if e.g. NOTAMs are active */
	active?: boolean;
};

/**
 * Settings for the location indicators (green bubbles) on the map
 */
export type TSettingsMapLocationIndicator = {
	size?: number;
	bigBubbleMultiplier?: number;
	hoverMultiplier?: number;
	hideLabels?: boolean;
	activeIndicator?: boolean; // Whether or not to color the dot depending on whether messages are active or not (default not)
	activeColor?: string;
};

/**
 * Settings for colors on NOTAM zones shown on map
 */
export type TSettingsMapZoneColors = {
	restrictedClosed?: string;
	restrictedClosedLater?: string;
	warning?: string;
	obstacle?: string;
	obstacleLight?: string;
};

/**
 * Sidebar menu contents. Pass children for use as a submenu.
 */
export type TSettingsAppMenu = {
	/** Display title */
	title: string;
	tooltip?: string;
	/**OnClick reference link */
	link?: string;
	/** Is {@link link} external? */
	external?: boolean;
	/**For Sigmets & NOTAM/Snowtam, render ICAO codes */
	children?: TSettingsAppMenu[];
};

type TMapOptions = Omit<mapboxgl.MapboxOptions, 'container'>;

export type TVFRSettings = {
	/* Mapbox ID for the tileset */
	tilesetId: string;
	/* Name for the source */
	sourceName: string;
	/* Setting to set layer visible */
	visible?: boolean;
};

/**App settings */
export type TSettings = {
	/**Mapbox settings */
	mapbox: TMapOptions;
	/** Map settings */
	map: {
		/**Zones to render on the map. Zone can either be a polygon or a point and its properties may hold {@link TSettingsMapZoneProperties} */
		zones: GeoJSON.FeatureCollection<GeoJSON.Polygon | GeoJSON.Point | GeoJSON.MultiPolygon, TSettingsMapZoneProperties>;
		zoneColors?: TSettingsMapZoneColors; //Colors for zones shown on map
		locationIndicator?: TSettingsMapLocationIndicator;
		altitudeFilterFeatureEnabled: boolean;
		icao: string; // The ICAO code to load AFTN messages for when determining the "activeness" of location indicators
		vfr: TVFRSettings[];
	};
	/** App settings */
	app: {
		/**In-app documents: Language --> Document name identifier --> documentId  */
		documents: {
			[key: string]: {
				disclaimer: string;
				/**DisclaimerView document */
				acceptTerms: string;
				/**Terms for using naviair.gl */
				userTerms: string;
				cookieTerms?: string;
				dataSources?: string;
				cookiesNecessary?: string;
				cookiesMarketing?: string;
				cookiesStatistical?: string;
				cookiesFunctional?: string;
			};
		};
		cookies: TSettingsAppCookie[];
		/** Sidebar menu to render and its contents */
		menu: TSettingsAppMenu[];
		/** Startup notification on initial load of application */
		startupNotification?: {
			/** Title in notification POPUP */
			title: string;
			/** Detailed text shown as description in POPUP, this should be as HTML */
			description: string;
			/** Duration of how long the information will be shown in second, when set to 0 it will never close automatically */
			duration: number;
			/** Active flag, set to true/false */
			active: boolean;
			/** Optional unix timestamp for when the notification should no longer be shown */
			expire?: number;
			/** Optional unix timestamp for when the notification should start being shown */
			begin?: number;
		};
	};
	availableAftnTypes: EAftnTypes[];
};

/**
 * Type for recording downtime on DCG (or any other system).
 * This is used to trigger a notification/banner on the page informing about the outage and the last heartbeat.
 * The message is the text shown on the page, when a downtime is registered.
 */
export type TDowntime = {
	isDown: boolean;
	message: string; 	// The text to show in the banner
}

/**
 * The configuration as exposed from the database for use in the project
 */
export interface IConfiguration {
	/**Configuration ID from database */
	_id: string;
	name: string;
	description: string;
	createdAt?: number;
	updatedAt?: number;
	deletedAt?: number;
	/**Configuration state */
	active: boolean;
	/**Maintenance state */
	maintenance: TMaintenence;
	/** Downtime alert */
	downtime: TDowntime;
	/** Domains allowed to request configuration */
	domains: TDomain[];
	/**Languages supported by configuration */
	languages: string[];
	/**Settings related to the application */
	settings: TSettings;
	/**Translations: Language --> Translations collection */
	translations: {
		[key: string]: ITranslation;
	};
	/**Pages: Page language --> Page name identifier --> Page */
	pages: {
		[key: string]: {
			[key: string]: IPage;
		};
	};
	/**All documents: DocumentID --> Document */
	documents: {
		[key: string]: IDocument;
	};
}

// The IPage in briefing application includes the title
export interface IPage<TypeIPageDataIcon = string> extends IPageUTM<TypeIPageDataIcon> {
	title: string;
}

//@naviair-utm-shared-interfaces exports
export { IDocument, ITranslation, TDomain, TMaintenence, TSettingsAppCookie };
